import { createActions, OnSignal } from 'dreamstate';

import { Definable, Id, Maybe, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { firebaseConfig } from '../../../../configs/FirebaseConfig';
import { EComposerSessionType } from '../../../../types/composer_session';
import { ELiveErrorCode } from '../../../../types/error';
import {
  IFirebaseDependencies,
  TFirebaseDatabase,
  TFirebaseReference,
} from '../../../../types/firebase';
import {
  EInteractionModule,
  IPoll,
  TInteractionHistoryConnection,
  TPollsList,
} from '../../../../types/interaction_tools';
import { IInteractionStoreInitialState } from '../../../../types/store';
import { LiveError } from '../../../../utils/error';
import {
  getDatabaseRef,
  getDatabaseRootRef,
  getRealtimeDatabase,
} from '../../../../utils/firebase/cruds';
import { checkModuleIsActive } from '../../../../utils/interaction_tools/module';
import { UnsubscribingContextManager } from '../../../../utils/store/unsubscribing_manager';
import {
  ELiveErrorCategory,
  trackLiveAction,
  withLiveErrorTracking,
} from '../../../../utils/tracking';
import {
  ELiveRealtimeSignal,
  ELiveSignal,
  TFirebaseInteractionAppReadySignalEvent,
} from '../../../signals';
import {
  createPoll,
  deletePoll,
  removeActivePoll,
  setActivePoll,
  updatePoll,
  voteForPollOption,
} from './PollManager.actions';
import { dispose } from './PollManager.operations';
import {
  hydrateActivePoll,
  hydratePollsList,
  subscribeToActivePoll,
  subscribeToPolls,
} from './PollManager.subscription';

export interface IPollContext {
  pollActions: {
    createPoll(pollData: Partial<IPoll>): Promise<void>;
    removeActivePoll(): Promise<void>;
    deletePoll(pollId: string): Promise<void>;
    updatePoll(pollId: string, poll: IPoll): Promise<void>;
    setActivePoll(pollId: string): Promise<void>;
    voteForPollOption(pollId: string, optionIndex: number): Promise<void>;
  };
  isHydrated: boolean;
  hydratedAt: Optional<number>;
  activePollId: Optional<Id>;
  polls: TPollsList;
  pollsHistory: Array<TInteractionHistoryConnection>;
}

/**
 * Polls manager, subscribe/unsubscribe from polls, close-open-push polls.
 *
 * Note:
 *  Currently every vote in poll causes updates of all subscribers and stores votes as array of ids.
 *  It is architectural limitation that should be handled in a separate collection with cloud functions.
 *  To solve it we just get count of keys and store number locally, but it requires additional attention.
 *  Make sure you are writing original model when updating/creating poll and exclude transformation fields.
 */
export class PollManager extends UnsubscribingContextManager<IPollContext> {
  public override context: IPollContext = {
    pollActions: createActions({
      createPoll: (pollData: IPoll) => this.createPoll(pollData),
      removeActivePoll: () => this.removeActivePoll(),
      deletePoll: (pollId: string) => this.deletePoll(pollId),
      updatePoll: (pollId: string, poll: IPoll) => this.updatePoll(pollId, poll),
      setActivePoll: (pollId: string) => this.setActivePoll(pollId),
      voteForPollOption: (pollId: string, optionIndex: number) =>
        this.voteForPollOption(pollId, optionIndex),
    }),
    isHydrated: false,
    hydratedAt: null,
    activePollId: null,
    polls: {},
    pollsHistory: [],
  };

  public readonly log: Logger = new Logger('🎨POLL');
  /**
   * Whether the poll module is active in the current context.
   */
  public readonly isModuleActive: boolean = false;
  public readonly composerSessionType: Definable<EComposerSessionType>;

  public firebase!: IFirebaseDependencies;
  public rootRef: Optional<TFirebaseReference> = null;
  public pollActiveMetaRef: Optional<TFirebaseReference> = null;
  public pollListRef: Optional<TFirebaseReference> = null;

  public constructor(initialState?: IInteractionStoreInitialState) {
    super();

    this.isModuleActive = checkModuleIsActive(
      initialState?.interaction?.feature?.module,
      EInteractionModule.POLL,
    );

    this.composerSessionType = initialState?.sessionType;
  }

  public override onProvisionEnded(): void {
    return dispose(this);
  }

  /**
   * Create poll object.
   * Make sure that written object is matching required DB model.
   */
  public async createPoll(pollData: IPoll): Promise<void> {
    return withLiveErrorTracking(() => createPoll(this, pollData), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'createPoll',
      data: { pollData },
    });
  }

  /**
   * Delete poll object from interaction tools collection.
   */
  public async deletePoll(pollId: string): Promise<void> {
    return withLiveErrorTracking(() => deletePoll(this, pollId), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'deletePoll',
      data: { pollId },
    });
  }

  /**
   * Update poll object.
   * Make sure that written object is matching required DB model.
   * Update only fields that are allowed to be updated and do not overwrite nested objects with side changes.
   */
  public async updatePoll(pollId: string, pollData: IPoll): Promise<void> {
    return withLiveErrorTracking(() => updatePoll(this, pollId, pollData), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'updatePoll',
      data: { pollId, pollData },
    });
  }

  /**
   * Set provided poll as active and show it for participants in chat.
   */
  public async setActivePoll(pollId: Optional<string>): Promise<void> {
    return withLiveErrorTracking(() => setActivePoll(this, pollId), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'setActivePoll',
      data: { pollId },
    });
  }

  /**
   * Remove active poll from selection.
   */
  public async removeActivePoll(): Promise<void> {
    return withLiveErrorTracking(() => removeActivePoll(this), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'removeActivePoll',
    });
  }

  /**
   * Vote for poll option.
   */
  public async voteForPollOption(pollId: string, optionIndex: number): Promise<void> {
    return withLiveErrorTracking(() => voteForPollOption(this, pollId, optionIndex), {
      category: ELiveErrorCategory.INTERACTION,
      method: 'voteForPollOption',
    });
  }

  /**
   * Assert poll existence and get representation or fail execution of code.
   */
  public assertPollExists(pollId: string): IPoll | never {
    const existingPoll: Maybe<IPoll> = this.context.polls[pollId];

    /**
     * Verify that updating existing and accounted poll.
     */
    if (existingPoll) {
      return existingPoll;
    } else {
      throw new LiveError('Cannot modify poll that does not exist.');
    }
  }

  /**
   * Assert current manager is initialized and can continue working with current scope.
   */
  public assertIsInitialized(): asserts this is {
    pollListRef: TFirebaseReference;
    pollActiveMetaRef: TFirebaseReference;
    rootRef: TFirebaseReference;
  } {
    if (!this.pollListRef || !this.pollActiveMetaRef || !this.rootRef) {
      throw new LiveError('Polls manager is not initialized.', {
        code: ELiveErrorCode.INITIALIZATION,
      });
    }
  }

  @OnSignal(ELiveRealtimeSignal.FIREBASE_INTERACTION_APP_READY)
  private async onChatApplicationReady({
    data: { firebase, app, roomId, metadata },
  }: TFirebaseInteractionAppReadySignalEvent): Promise<void> {
    this.firebase = firebase;

    if (this.isModuleActive) {
      this.log.info('🚀Initializing poll connection:', roomId);
      trackLiveAction('fb_init_poll_manager');

      const database: TFirebaseDatabase = getRealtimeDatabase(firebase, app);

      this.pollActiveMetaRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.POLLS.ACTIVE(roomId),
      );
      this.pollListRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.POLLS.LIST(roomId),
      );
      this.rootRef = getDatabaseRootRef(firebase, database);

      await Promise.all([
        hydrateActivePoll(this, this.pollActiveMetaRef),
        hydratePollsList(this, this.pollListRef),
      ]).finally(() => {
        this.log.info('Hydration finished');
        this.setContext({
          isHydrated: true,
          hydratedAt: null,
          pollsHistory: metadata?.connections?.pollsHistory || [],
        });
      });

      this.addUnSubscribers([
        subscribeToPolls(this, this.pollListRef),
        subscribeToActivePoll(this, this.pollActiveMetaRef),
      ]);
    } else {
      this.log.info('🚀Skipping poll connection');
    }
  }

  /**
   * On logout clean everything up.
   */
  @OnSignal(ELiveSignal.INTERACTION_SESSION_LOGOUT)
  private onInteractionSessionLogout(): void {
    return dispose(this);
  }
}
